<template>
  <template v-if="enableReachAndFrequencyData && showReachAndFrequencyData">
    <template v-if="!useSlot">
      <div>
        <KPI
          data-test-id="rf-summary-reach"
          :label="reachLabel"
          :value="reachFormatted"
          trend-format="+0a"
          trend-style="font-size: 0.9em"
          :icon="iconColoredReach"
          :is-loading="isLoading || state.isReachAndFrequencyLoading"
          :big-icon="hasBigIcon"
          :value-style="valueStyle"
          :label-style="labelStyle" />
      </div>
      <hr v-if="showSeparator" />
      <div>
        <KPI
          v-if="state.frequencyAvg !== null"
          data-test-id="rf-summary-frequency"
          :label="frequencyLabel"
          :value="frequencyFormatted"
          trend-format="+0a"
          trend-style="font-size: 0.9em"
          :icon="iconColoredFrequency"
          :is-loading="isLoading || state.isReachAndFrequencyLoading"
          :big-icon="hasBigIcon"
          :value-style="valueStyle"
          :label-style="labelStyle" />
      </div>
    </template>
    <template v-else>
      <slot
        :reach="state.reachNet ? reachFormatted : undefined"
        :frequency="state.frequencyAvg ? frequencyFormatted : undefined"
        :loading="state.isReachAndFrequencyLoading" />
    </template>
  </template>
</template>

<script setup lang="ts">
  import axios from 'axios';
  import { capitalize, computed, markRaw, reactive, ref, watch } from 'vue';

  import { IconColoredPacing, IconColoredTargeting } from '@/common/icons/colored';
  import { LineItem, Package, Plan } from '@/common/models';
  import { isNil, toServerDate } from '@/globals';
  import KPI from '@/org-app/components/controls/KPI.vue';
  import formatNumber from '@/utils/NumberFormatter';
  import { mapGetters, mapOrgAppActions } from '@/utils/StateMapping';
  import ThrottledTaskRunner from '@/utils/ThrottledTaskRunner';
  import i18n from '@/vueI18n';

  interface State {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    cancelReachAndFrequencyTokenSource: any | null;
    frequencyAvg: number | null;
    isReachAndFrequencyLoading: boolean;
    reachNet: number | null;
  }

  // Constants
  const DEBOUNCE_TIME = 1500;
  const frequencyLabel = capitalize(i18n.global.t('names.estimatedFrequency', { n: 1 }));
  const iconColoredReach = markRaw(IconColoredTargeting);
  const iconColoredFrequency = markRaw(IconColoredPacing);
  const reachLabel = capitalize(i18n.global.t('names.estimatedReach', { n: 1 }));
  const na = 'N/A';

  // Props
  // The component either takes an entity (lineItem) and will fetch reach and frequency
  // or takes reach and frequency numbers from the parent and will display them
  const props = withDefaults(
    defineProps<{
      entity?: LineItem | Plan | Package;
      reach?: number;
      maxReach?: number;
      frequency?: number;
      hasBigIcon?: boolean;
      isLoading?: boolean;
      showSeparator?: boolean;
      useSlot?: boolean;
      valueStyle?: string;
    }>(),
    {
      entity: null,
      reach: null,
      maxReach: null,
      frequency: null,
      hasBigIcon: false,
      isLoading: false,
      showSeparator: false,
      useSlot: false,
      valueStyle: null,
    },
  );

  // Store
  const { membershipSettings } = mapGetters();
  const { getEstimatedReachStatistics, getTargetedUnitAndSiteIDs } = mapOrgAppActions();

  // Constants
  const BATCH_COUNT = 50000;

  // Reactive state
  const state = reactive<State>({
    cancelReachAndFrequencyTokenSource: null,
    frequencyAvg: null,
    isReachAndFrequencyLoading: false,
    reachNet: null,
  });
  const lastTargetParams = ref(null);

  // Computed
  const labelStyle = computed<string>(() => (props.hasBigIcon ? 'font-size: 1.3rem;' : 'font-size: 1rem;'));
  const frequencyFormatted = computed<string>(() => formatNumber(state.frequencyAvg, '0.00'));
  const reachFormatted = computed<string>(() => formatToThousands(state.reachNet));
  const enableReachAndFrequencyData = computed<boolean>(() => {
    return membershipSettings.value?.uiEnableForecastedReach;
  });
  const hasReachAndFrequencyData = computed<boolean>(() => {
    return state.reachNet !== null || state.frequencyAvg !== null;
  });
  const showReachAndFrequencyData = computed(() => {
    return state.isReachAndFrequencyLoading || hasReachAndFrequencyData.value;
  });
  const targetImpressions = computed<number>(() =>
    props.entity instanceof Package
      ? props.entity.cpmCalculator.estimatedTargetImpressions
      : props.entity?.targetImpressions,
  );

  // Methods
  const formatToThousands = (value: number) => {
    if (value >= 1000) {
      return formatNumber(value, '0.0a');
    }
    return formatNumber(value, { mantissa: 0 });
  };
  const refreshReachAndFrequencyInfo = new ThrottledTaskRunner(async (): Promise<void> => {
    state.isReachAndFrequencyLoading = true;

    if (!enableReachAndFrequencyData.value) {
      state.isReachAndFrequencyLoading = false;
      return;
    }

    // Early return with no entity or same params as last time
    const { entity } = props;
    if (!entity || entity.reachTargetParams === lastTargetParams.value) {
      state.isReachAndFrequencyLoading = false;
      return;
    }

    if (state.cancelReachAndFrequencyTokenSource != null) {
      state.cancelReachAndFrequencyTokenSource.cancel();
    }

    state.cancelReachAndFrequencyTokenSource = axios.CancelToken.source();

    const { startDate, endDate } = entity;
    const endDateExclusive = endDate.plus({ days: 1 });

    // When budget is cleared, for some reason target impressions becomes an array [0]
    if (!entity.targetParams || !targetImpressions.value || Array.isArray(targetImpressions.value)) {
      state.reachNet = null;
      state.frequencyAvg = null;
      state.isReachAndFrequencyLoading = false;
      return;
    }

    const targetedUnitAndSiteIDs = await getTargetedUnitAndSiteIDs({
      data: entity.targetParamsEstimatedDeliveryStatistics,
      cancelTokenSource: state.cancelReachAndFrequencyTokenSource,
    });
    const unitIds = targetedUnitAndSiteIDs.unitIDs;

    if (!unitIds.length) {
      state.reachNet = null;
      state.frequencyAvg = null;
      state.isReachAndFrequencyLoading = false;
      return;
    }

    const baseParams = {
      ...entity.reachTargetParams,
      date_from: toServerDate(startDate),
      date_to: toServerDate(endDateExclusive),
    };
    try {
      const promises = [];
      for (let i = 0; i < unitIds.length; i += BATCH_COUNT) {
        const endIdx = Math.min(unitIds.length, i + BATCH_COUNT);
        const unit_ids = unitIds.slice(i, endIdx);
        promises.push(
          getEstimatedReachStatistics({
            params: { ...baseParams, unit_ids },
            cancelTokenSource: state.cancelReachAndFrequencyTokenSource,
          }),
        );
      }

      const response = await Promise.all(promises);
      const reach = response.reduce((sum, stats) => {
        return sum + (stats?.reach ?? -1);
      }, 0);
      lastTargetParams.value = { ...baseParams, unit_ids: unitIds };

      if (reach < 0) {
        // this can happen when call is canceled
        state.reachNet = null;
        state.frequencyAvg = null;
        return;
      } else if (reach === 0) {
        state.reachNet = null;
        state.frequencyAvg = null;
        state.isReachAndFrequencyLoading = false;
        return;
      }

      state.reachNet = reach;
      state.frequencyAvg = targetImpressions.value / reach;
      state.isReachAndFrequencyLoading = false;
      /* eslint-disable  @typescript-eslint/no-explicit-any */
    } catch (err: any) {
      state.reachNet = null;
      state.frequencyAvg = null;
      if (!axios.isCancel(err)) {
        state.isReachAndFrequencyLoading = false;
      }
    }
  }, DEBOUNCE_TIME);

  // Watchers
  watch(
    () => props.entity?.reachTargetParams,
    () => {
      refreshReachAndFrequencyInfo.run();
    },
    {
      immediate: true,
    },
  );
  watch(
    () => props.entity?.startDate,
    () => refreshReachAndFrequencyInfo.run(),
  );
  watch(
    () => props.entity?.endDate,
    () => refreshReachAndFrequencyInfo.run(),
  );
  watch(
    () => props.reach,
    (newReach) => {
      if (!isNil(newReach)) {
        state.reachNet = newReach || null;
      }
    },
  );
  watch(
    () => props.frequency,
    (newFrequency) => {
      if (!isNil(newFrequency)) {
        state.frequencyAvg = newFrequency || null;
      }
    },
  );
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
  @use '@/styles/variables' as *;

  .separator {
    width: 0.1rem;
    height: 5.2rem;
    border-left: dotted 0.1rem $borders;
    min-width: 0.1rem;
    max-width: 0.1rem;
    margin: 0 1rem;
  }
</style>
